import {
    BadgeCheck,
    Check,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Download,
    Headphones,
    Info,
    MapPin,
    MessageCircle,
    ReceiptText,
    ShieldCheck,
    Star,
    Truck,
    UserRound,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
    fetchOrderById,
    clearCurrentOrder,
    fetchOrderTracking,
} from "../../../Redux/custmoer_orders/slice";
import { Order } from "../../../Redux/custmoer_orders/types";
import { transformOrderToSalarOrder } from "../OrdersPage/dataTransformers";
import { OrderItem, SalarOrder } from "../OrdersPage/OrdersTypes";
import { TrackingStageId } from "./TrackPackageTypes";
import "./TrackPackagePage.css";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import { useCart } from "../../../context/CartContext";
import ChatPanel from "../../globalcomponents/ChatPanel/ChatPanel";

const stages: { id: TrackingStageId; label: string }[] = [
    { id: "confirmed", label: "Order Confirmed" },
    { id: "shipped", label: "Shipped" },
    { id: "out-for-delivery", label: "Out for Delivery" },
    { id: "delivered", label: "Delivered" },
];

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(value);

const PLACEHOLDER_IMAGE = "/assets/images/logos/salarlogo.png";

const formatDate = (dateStr: string) => {
    try {
        return new Date(dateStr).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return dateStr;
    }
};

const formatTime = (dateStr: string) => {
    try {
        return new Date(dateStr).toLocaleTimeString("en-IN", {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return "";
    }
};

const statusToStage = (status: string): TrackingStageId => {
    const s = status?.toUpperCase();
    if (s === "DELIVERED") return "delivered";
    if (s === "OUT_FOR_DELIVERY" || s === "RIDER_ARRIVED_AT_LOCATION") return "out-for-delivery";
    if (s === "SHIPPED" || s === "IN_TRANSIT" || s === "ORDER_PICKED_UP") return "shipped";
    return "confirmed";
};

const getProductRoute = (order: SalarOrder, item: OrderItem): string => {
    const isReferral = order.purchaseType === "MLM" || item.sourceType === "REFERRAL";
    return isReferral
        ? `/Refproductdetails/${item.productId}`
        : `/DetailPage/${item.productId}`;
};

const TrackPackagePage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { cartCount } = useCart();
    const { orderId } = useParams<{ orderId: string }>();

    const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);
    const [updatesOpen, setUpdatesOpen] = useState(false);
    const [experienceOpen, setExperienceOpen] = useState(false);
    const [sellerFeedbackOpen, setSellerFeedbackOpen] = useState(false);
    const [experienceRating, setExperienceRating] = useState(0);
    const [sellerRating, setSellerRating] = useState(0);
    const [experienceText, setExperienceText] = useState("");
    const [sellerFeedbackText, setSellerFeedbackText] = useState("");
    const [chatOpen, setChatOpen] = useState(false);

    // Redux state
    const { currentOrder, tracking, loading, error } = useSelector(
        (state: RootState) => state.customerOrders,
    );

    // Fetch order + tracking on mount
    useEffect(() => {
        if (orderId) {
            dispatch(fetchOrderById(orderId));
            dispatch(fetchOrderTracking(orderId));
        }
        return () => {
            dispatch(clearCurrentOrder());
        };
    }, [orderId, dispatch]);

    // Transform API order to UI order
    const order = useMemo<SalarOrder | null>(() => {
        if (!currentOrder) return null;
        return transformOrderToSalarOrder(currentOrder as Order);
    }, [currentOrder]);

    const firstItem = order?.items[0];

    // Build tracking updates from tracking history
    const trackingUpdates = useMemo(() => {
        if (!tracking?.trackingHistory?.length) return [];
        return tracking.trackingHistory.map((h, idx) => ({
            id: `update-${idx}`,
            stage: statusToStage(h.status),
            title: h.remarks || h.status,
            description: h.remarks || h.status,
            location: h.location || "",
            date: formatDate(h.timestamp),
            time: formatTime(h.timestamp),
            completed: true,
        }));
    }, [tracking]);

    const currentStage: TrackingStageId = tracking
        ? statusToStage(tracking.currentStatus)
        : "confirmed";

    const currentStageIndex = stages.findIndex((s) => s.id === currentStage);
    const delivered = currentStage === "delivered";

    // Build address from tracking data
    const address = tracking?.deliveryAddress;
    const hasAddress = address && (address.addressLine1 || address.city);

    // Build pricing from tracking data
    const pricing = tracking?.pricing;

    // Build product summary
    const productSummary = tracking?.productSummary;

    const openProduct = () => {
        if (!order || !firstItem) return;
        navigate(getProductRoute(order, firstItem));
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.src = PLACEHOLDER_IMAGE;
    };

    const stageState = (index: number) => {
        if (delivered) return "complete";
        if (index < currentStageIndex) return "complete";
        if (index === currentStageIndex) return "active";
        return "upcoming";
    };

    // Loading state
    if (loading && !order) {
        return (
            <>
                <div className="track-package-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
                <div className="track-package-desktop-secondary-navbar">
                    <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)} />
                </div>
                <main className="track-package-page">
                    <div className="track-package-shell">
                        <div className="track-loading">
                            <div className="track-spinner"></div>
                            <p>Loading tracking information...</p>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    // Error state
    if (error && !order) {
        return (
            <>
                <div className="track-package-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
                <div className="track-package-desktop-secondary-navbar">
                    <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)} />
                </div>
                <main className="track-package-page">
                    <div className="track-package-shell">
                        <div className="track-error">
                            <p>{error}</p>
                            <button type="button" onClick={() => {
                                if (orderId) {
                                    dispatch(fetchOrderById(orderId));
                                    dispatch(fetchOrderTracking(orderId));
                                }
                            }}>
                                Retry
                            </button>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    if (!order || !firstItem) {
        return (
            <>
                <div className="track-package-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
                <div className="track-package-desktop-secondary-navbar">
                    <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)} />
                </div>
                <main className="track-package-page">
                    <div className="track-package-shell">
                        <div className="track-error">
                            <p>Order not found</p>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    const latestUpdate = trackingUpdates[trackingUpdates.length - 1];
    const headline = delivered ? "Delivered" : order.statusTitle;
    const message = delivered ? "Your package has been delivered successfully." : order.statusMessage;
    const courier = tracking?.courierPartner || "Salar Fulfilment";
    const trackingId = tracking?.trackingNumber || order.trackingNumber || "N/A";
    const expectedText = tracking?.estimatedDeliveryDate
        ? `Expected by ${formatDate(tracking.estimatedDeliveryDate)}`
        : order.expectedDate || "Estimated delivery pending";

    return (
        <>
            <div className="track-package-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
            <div className="track-package-desktop-secondary-navbar">
                    <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)} />
                </div>
            <main className="track-package-page">
                <div className="track-package-shell">
                    <div className="track-breadcrumb">
                        <button type="button" onClick={() => navigate("/Orders")}>
                            Your Orders
                        </button>
                        <ChevronRight size={15} />
                        <strong>Track Package</strong>
                    </div>

                    <section className="track-heading">
                        <div>
                            <span>LIVE ORDER JOURNEY</span>
                            <h1>Track Package</h1>
                            <p>Order {order.orderNo || order.id} · Tracking ID {trackingId}</p>
                        </div>
                        <div className="track-heading-status">
                            <span className={delivered ? "delivered" : "moving"}>
                                {delivered ? "DELIVERED" : "IN TRANSIT"}
                            </span>
                            <strong>{headline}</strong>
                            <small>{expectedText}</small>
                        </div>
                    </section>

                    <div className="track-layout">
                        <div className="track-main-column">
                            <section className="track-card track-journey-card">
                                <div className="track-product-panel">
                                    <button type="button" className="track-product-image" onClick={openProduct}>
                                        <img
                                            src={productSummary?.productImage || firstItem.image}
                                            alt={productSummary?.productName || firstItem.title}
                                            onError={handleImageError}
                                        />
                                    </button>
                                    <div className="track-product-copy">
                                        <button type="button" className="track-product-description" onClick={openProduct}>
                                            <strong>{productSummary?.productName || firstItem.title}</strong>
                                            <span>{firstItem.description}</span>
                                        </button>
                                        <div className="track-product-attributes">
                                            <span>Quantity<strong>{productSummary?.quantity || firstItem.quantity}</strong></span>
                                        </div>
                                        <button type="button" className="track-seller-link" onClick={() => navigate(`/seller/${firstItem.sellerSlug}`)}>
                                            Sold by <strong>{firstItem.sellerName}</strong>
                                            <BadgeCheck size={15} />
                                        </button>
                                    </div>
                                </div>

                                <div className="track-journey-head">
                                    <div>
                                        <span>PACKAGE PROGRESS</span>
                                        <h2>{headline}</h2>
                                        <p>{message}</p>
                                    </div>
                                    <div>
                                        <Truck size={22} />
                                        <span>{courier}</span>
                                    </div>
                                </div>

                                <div className="track-horizontal">
                                    {stages.map((stage, index) => {
                                        const state = stageState(index);
                                        return (
                                            <div className={`track-step ${state}`} key={stage.id}>
                                                <div className="track-step-rail">
                                                    <div className="track-step-dot">
                                                        {state === "complete" ? (
                                                            <Check size={15} strokeWidth={3} />
                                                        ) : (
                                                            <span />
                                                        )}
                                                    </div>
                                                    {index < stages.length - 1 && (
                                                        <div className="track-step-line" />
                                                    )}
                                                </div>
                                                <strong>{stage.label}</strong>
                                                <small>
                                                    {state === "complete"
                                                        ? "Completed"
                                                        : state === "active"
                                                            ? "Current stage"
                                                            : "Pending"}
                                                </small>
                                            </div>
                                        );
                                    })}
                                </div>

                                {latestUpdate && (
                                    <div className="track-latest-update">
                                        <div className="track-latest-icon">
                                            {delivered ? (
                                                <CheckCircle2 size={20} />
                                            ) : (
                                                <span className="track-live-pulse">
                                                    <i />
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <span>LATEST UPDATE</span>
                                            <strong>{latestUpdate.title}</strong>
                                            <p>{latestUpdate.description}</p>
                                        </div>
                                    </div>
                                )}

                                {trackingUpdates.length > 0 && (
                                    <button
                                        type="button"
                                        className="track-see-updates"
                                        onClick={() => setUpdatesOpen((c) => !c)}
                                    >
                                        <span>{updatesOpen ? "Hide shipping updates" : "See all updates"}</span>
                                        <ChevronDown size={18} />
                                    </button>
                                )}

                                {updatesOpen && trackingUpdates.length > 0 && (
                                    <div className="track-updates-expanded">
                                        <div className="track-shipping-summary">
                                            <div>
                                                <span>SHIPPING DETAILS</span>
                                                <h3>Complete package journey</h3>
                                            </div>
                                            <div>
                                                <span>Carrier</span>
                                                <strong>{courier}</strong>
                                            </div>
                                            <div>
                                                <span>Tracking ID</span>
                                                <strong>{trackingId}</strong>
                                            </div>
                                        </div>
                                        <div className="track-update-timeline">
                                            {[...trackingUpdates].reverse().map((update, index) => (
                                                <article className={index === 0 ? "current" : ""} key={update.id}>
                                                    <div className="track-update-marker">
                                                        <span />
                                                    </div>
                                                    <div className="track-update-time">
                                                        <strong>{update.date}</strong>
                                                        <span>{update.time}</span>
                                                    </div>
                                                    <div className="track-update-copy">
                                                        <strong>{update.title}</strong>
                                                        <p>{update.description}</p>
                                                        {update.location && (
                                                            <span>
                                                                <MapPin size={14} />
                                                                {update.location}
                                                            </span>
                                                        )}
                                                    </div>
                                                </article>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {tracking?.trackingUrl && (
                                    <div className="track-courier-link-row">
                                        <a
                                            href={tracking.trackingUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <Truck size={17} />
                                            Track on courier website
                                        </a>
                                    </div>
                                )}

                                <div className="track-help-row">
                                    <div>
                                        <Headphones size={20} />
                                        <span>Need help with this package?</span>
                                    </div>
                                    <button type="button" onClick={() => setChatOpen(true)}>
                                        <MessageCircle size={17} />
                                        Chat With Us
                                    </button>
                                </div>
                            </section>

                            <section className="track-card track-rating-card">
                                <div className="track-rating-heading">
                                    <div>
                                        <span>RATE YOUR EXPERIENCE</span>
                                        <h2>How is your delivery experience?</h2>
                                        <p>Your feedback helps Salar improve order tracking and delivery quality.</p>
                                    </div>
                                    <div className="track-stars">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                            <button
                                                type="button"
                                                key={star}
                                                className={star <= experienceRating ? "selected" : ""}
                                                onClick={() => setExperienceRating(star)}
                                            >
                                                <Star size={25} fill={star <= experienceRating ? "currentColor" : "none"} />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="track-feedback-accordion"
                                    onClick={() => setExperienceOpen((c) => !c)}
                                >
                                    <div>
                                        <MessageCircle size={18} />
                                        <span>Add delivery experience details</span>
                                    </div>
                                    <ChevronDown size={18} />
                                </button>

                                {experienceOpen && (
                                    <div className="track-feedback-body">
                                        <label>What went well or what could be better?</label>
                                        <textarea
                                            value={experienceText}
                                            onChange={(e) => setExperienceText(e.target.value)}
                                            placeholder="Share a short note about tracking, delivery timing, package handling or the delivery experience."
                                        />
                                        <button type="button">Submit Experience</button>
                                    </div>
                                )}

                                <div className="track-seller-feedback">
                                    <button
                                        type="button"
                                        className="track-feedback-accordion"
                                        onClick={() => setSellerFeedbackOpen((c) => !c)}
                                    >
                                        <div>
                                            <ShieldCheck size={18} />
                                            <span>Seller Feedback · {firstItem.sellerName}</span>
                                        </div>
                                        <ChevronDown size={18} />
                                    </button>

                                    {sellerFeedbackOpen && (
                                        <div className="track-feedback-body">
                                            <label>Rate your experience with the seller</label>
                                            <div className="track-stars seller-stars">
                                                {[1, 2, 3, 4, 5].map((star) => (
                                                    <button
                                                        type="button"
                                                        key={star}
                                                        className={star <= sellerRating ? "selected" : ""}
                                                        onClick={() => setSellerRating(star)}
                                                    >
                                                        <Star size={23} fill={star <= sellerRating ? "currentColor" : "none"} />
                                                    </button>
                                                ))}
                                            </div>
                                            <textarea
                                                value={sellerFeedbackText}
                                                onChange={(e) => setSellerFeedbackText(e.target.value)}
                                                placeholder="Tell us about seller communication, item accuracy, packaging or fulfilment."
                                            />
                                            <button type="button">Continue to Seller Feedback</button>
                                        </div>
                                    )}
                                </div>
                            </section>
                        </div>

                        <aside className="track-side-column">
                            <section className="track-side-card track-delivery-card">
                                <div className="track-side-heading">
                                    <MapPin size={21} />
                                    <div>
                                        <span>DELIVERY DETAILS</span>
                                        <h2>Delivery Address</h2>
                                    </div>
                                </div>
                                {hasAddress ? (
                                    <>
                                        <div className="track-address">
                                            {address!.addressLine1 && <strong>{address!.addressLine1}</strong>}
                                            {address!.addressLine2 && <span>{address!.addressLine2}</span>}
                                            {address!.landmark && <span>Landmark: {address!.landmark}</span>}
                                            <span>
                                                {address!.city}{address!.city && address!.state ? ", " : ""}
                                                {address!.state} {address!.pincode}
                                            </span>
                                            <span>{address!.country || "India"}</span>
                                        </div>
                                        <div className="track-recipient">
                                            <UserRound size={19} />
                                            <div>
                                                <span>RECIPIENT</span>
                                                <strong>{address!.fullName || "Customer"}</strong>
                                                {address!.mobile && <p>{address!.mobile}</p>}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="track-address">
                                        <span className="track-address-placeholder">Address not available</span>
                                    </div>
                                )}
                            </section>

                            <section className="track-side-card track-price-card">
                                <div className="track-side-heading">
                                    <ReceiptText size={21} />
                                    <div>
                                        <span>PAYMENT SUMMARY</span>
                                        <h2>Price Details</h2>
                                    </div>
                                </div>
                                <div className="track-payment-method">
                                    <span>Payment Method</span>
                                    <strong>{order.paymentMethod}</strong>
                                </div>
                                {pricing && (
                                    <>
                                        <dl className="track-price-list">
                                            <div>
                                                <dt>Listing Price</dt>
                                                <dd>₹{formatPrice(pricing.listingPrice)}</dd>
                                            </div>
                                            <div>
                                                <dt>Selling Price</dt>
                                                <dd>₹{formatPrice(pricing.sellingPrice)}</dd>
                                            </div>
                                            <div>
                                                <dt>Product Discount</dt>
                                                <dd className="discount">-₹{formatPrice(pricing.discount)}</dd>
                                            </div>
                                            <div>
                                                <dt>Delivery Fee</dt>
                                                <dd>
                                                    {pricing.deliveryFee === 0
                                                        ? "FREE"
                                                        : `₹${formatPrice(pricing.deliveryFee)}`}
                                                </dd>
                                            </div>
                                            <div>
                                                <dt>Platform Fee</dt>
                                                <dd>₹{formatPrice(pricing.platformFee)}</dd>
                                            </div>
                                        </dl>
                                        <div className="track-total">
                                            <span>Total Paid</span>
                                            <strong>₹{formatPrice(pricing.totalPaid)}</strong>
                                        </div>
                                    </>
                                )}
                                {order.invoiceAvailable && (
                                    <button
                                        type="button"
                                        className="track-invoice-button"
                                        onClick={() => navigate(`/Invoice/${order.id}`)}
                                    >
                                        <Download size={17} />
                                        Download Invoice
                                    </button>
                                )}
                                <div className="track-secure-payment">
                                    <ShieldCheck size={17} />
                                    <span>Payment protected by Salar Secure Transaction.</span>
                                </div>
                            </section>

                            <section className="track-side-card track-order-reference">
                                <div className="track-side-heading">
                                    <Info size={21} />
                                    <div>
                                        <span>ORDER REFERENCE</span>
                                        <h2>Shipment Details</h2>
                                    </div>
                                </div>
                                <dl>
                                    <div>
                                        <dt>Order No</dt>
                                        <dd>{order.orderNo || order.id}</dd>
                                    </div>
                                    <div>
                                        <dt>Tracking ID</dt>
                                        <dd>{trackingId}</dd>
                                    </div>
                                    <div>
                                        <dt>Shipped Via</dt>
                                        <dd>{courier}</dd>
                                    </div>
                                    {tracking?.awbNumber && (
                                        <div>
                                            <dt>AWB Number</dt>
                                            <dd>{tracking.awbNumber}</dd>
                                        </div>
                                    )}
                                </dl>
                            </section>
                        </aside>
                    </div>
                </div>
            </main>

            <ChatPanel
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                order={currentOrder as any}
            />
        </>
    );
};

export default TrackPackagePage;
